<template>
  <div>
    <div class="router-header flex flex-wrap items-center mb-3">
      <div class="content-area__heading pr-4 border-0 md:border-r border-solid border-grey-light">
      </div>
    </div>
    
    <div class="vx-row">
      <div
        v-for="comp in pendingReview"
        :key="comp.i"
        class="vx-col w-full"
      >
        <div @click="goToDetails(comp)" class="block">
          <div class="vx-card overflow-hidden mb-4 hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm rounded-lg">
            <div class="p-4 flex flex-col justify-start text-left">
              
              <!-- TOP: Avatar & Title (Always visible) -->
              <div class="flex items-center w-full">
                <!-- Avatar / Icon -->
                <div style="position: relative; display: inline-flex;">
                  <span
                    class="inline-flex rounded-full select-none text-primary mc-icon"
                    style="background: rgba(var(--vs-primary),0.15); width: 45px; height: 45px; display: flex; align-items: center; justify-content: center;"
                  >
                    <img :src="comp.icon" alt="" width="24px" height="24px" />
                  </span>
                </div>

                <!-- Title & Status Badge -->
                <div class="truncate ml-3 flex-grow text-left">
                  <h5 class="mb-1 text-left font-semibold text-gray-800" style="font-size: 13px; white-space: normal;">
                    {{ comp.comp }}
                  </h5>
                  <div class="flex items-center space-x-2 mt-1">
                    <span class="px-2 py-0.5 bg-warning/20 text-warning font-bold rounded" style="font-size: 9px; letter-spacing: 0.5px;">
                      PENDING REVIEW
                    </span>
                  </div>
                </div>
              </div>

              <!-- BOTTOM: Proof of Work Actions (Fetched dynamically) -->
              <div 
                v-if="proofs[comp.i]" 
                class="mt-4 bg-white border border-gray-200 rounded-md p-3 w-full shadow-inner" 
                @click.stop
              >
                <div class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 border-b border-gray-100 pb-1">
                  Submitted Proof
                </div>
                
                <!-- Founder Notes -->
                <div v-if="proofs[comp.i].notes" class="text-xs text-gray-600 italic mb-3 bg-gray-50 p-2 rounded border border-gray-100 leading-relaxed">
                  "{{ proofs[comp.i].notes }}"
                </div>

                <!-- Action Buttons: View Docs & Approve -->
                <div class="flex flex-col gap-2 mt-2">
                  
                  <!-- View Document -->
                  <vs-button 
                    v-if="proofs[comp.i].fileUrl" 
                    type="border" 
                    color="primary" 
                    icon-pack="feather" 
                    icon="icon-file-text" 
                    size="small" 
                    class="w-full font-semibold"
                    @click.stop="openUrl(getPythonFileUrl(proofs[comp.i].fileUrl))"
                  >
                    View Uploaded Document
                  </vs-button>
                  
                  <!-- View Link -->
                  <vs-button 
                    v-if="proofs[comp.i].externalLink" 
                    type="border" 
                    color="primary" 
                    icon-pack="feather" 
                    icon="icon-external-link" 
                    size="small" 
                    class="w-full font-semibold"
                    @click.stop="openUrl(proofs[comp.i].externalLink)"
                  >
                    Open External Link
                  </vs-button>

                  <!-- Approve Button -->
                  <vs-button 
                    color="#28C76F" 
                    icon-pack="feather" 
                    icon="icon-check-circle" 
                    size="small" 
                    class="w-full font-bold mt-1 shadow-md"
                    @click.stop="approveGoal(comp.i)"
                  >
                    Approve & Complete
                  </vs-button>
                  
                </div>
              </div>
              
              <!-- Loading State -->
              <div v-else-if="isLoading[comp.i]" class="mt-4 text-xs text-gray-400 italic text-center w-full">
                Loading proof details...
              </div>
              
              <!-- Fallback if user clicked "Bypass" or backend failed -->
              <div v-else class="mt-4 w-full" @click.stop>
                 <vs-button 
                    color="#28C76F" 
                    type="flat"
                    icon-pack="feather" 
                    icon="icon-check-circle" 
                    size="small" 
                    class="w-full font-bold bg-green-50"
                    @click.stop="approveGoal(comp.i)"
                  >
                    Approve (No Attachment Found)
                  </vs-button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import constants from "../../../../constants.js";

export default {
  name: "PendingReview",
  props: ['pendingReview'],
  data() {
    return {
      constants,
      proofs: {}, // Stores proof data mapped by trainee_competencies_id (comp.i)
      isLoading: {} 
    };
  },
  watch: {
    pendingReview: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          newVal.forEach(comp => {
            if (comp.i && !this.proofs[comp.i]) {
              this.fetchProofOfWork(comp.i);
            }
          });
        }
      }
    }
  },
  methods: {
    async fetchProofOfWork(traineeCompetenciesId) {
      this.$set(this.isLoading, traineeCompetenciesId, true);
      try {
        const res = await this.$store.dispatch('proof-of-work/find', {
          query: {
            traineeCompetenciesId: traineeCompetenciesId,
            $sort: { createdAt: -1 } 
          }
        });

        if (res.data && res.data.length > 0) {
          this.$set(this.proofs, traineeCompetenciesId, res.data[0]);
        } else {
           // Explicitly set to null so the loading state goes away
           this.$set(this.proofs, traineeCompetenciesId, null);
        }
      } catch (error) {
        console.error("Could not fetch proof of work for:", traineeCompetenciesId, error);
        this.$set(this.proofs, traineeCompetenciesId, null);
      } finally {
        this.$set(this.isLoading, traineeCompetenciesId, false);
      }
    },
    
    // Safely constructs the URL to your Python Backend for the file
    getPythonFileUrl(path) {
      if (!path) return "";
      if (path.startsWith("http")) return path; 
      
      const pythonApiUrl =  'https://parser.ai.getboarded.com';
      return `${pythonApiUrl}${path.startsWith('/') ? '' : '/'}${path}`;
    },

    openUrl(url) {
      if(url) {
        window.open(url, '_blank');
      }
    },

    async approveGoal(compId) {
      try {
        // 1. Update Goal Status in backend
        const goalCompleteObj = { id: compId, progressStatus: "complete" };
        
        await this.$store.dispatch("endorsementInvite/markGoalComplete", goalCompleteObj);
        
        // 2. (Optional) Mark proof as approved in your new table
        if (this.proofs[compId]) {
           await this.$store.dispatch("proof-of-work/patch", [
             this.proofs[compId].id,
             { status: "approved" }
           ]);
        }

        this.$vs.notify({
          title: 'Goal Approved',
          text: 'The founder\'s goal has been marked as complete.',
          color: 'success',
          iconPack: 'feather',
          icon: 'icon-check'
        });

        // Reload the window or emit an event to refresh the lists so it moves to the "Completed" tab
        setTimeout(() => {
          window.location.reload();
        }, 1000);

      } catch(err) {
        console.error("Error approving goal:", err);
        this.$vs.notify({
          title: 'Error',
          text: 'Could not approve goal at this time.',
          color: 'danger'
        });
      }
    },

    goToDetails(comp) {
      const url = this.getSkillDetailsUrl(comp);
      window.open(url, '_blank');
    },

    getSkillDetailsUrl(comp) {
      const id = comp.id || comp.i; 
      let url = `${this.constants.url}/user/skill-details/${id}`;
      
      const name = this.traineeName || comp.traineeName || this.$route.query.traineeName;
      if (name) {
        url += `?traineeName=${encodeURIComponent(name)}&mentorView=true`;
      } else {
        url += `?mentorView=true`;
      }
      return url;
    }
  }
};
</script>

<style scoped>
.vx-card { cursor: pointer; }
.vx-card:hover { background: #F9F9F9; }
.truncate h5 { margin-left: 0 !important; }
</style>